import "@/components/global/components/tiptap/tiptap-content.css";

import { Box, Stack, Typography } from "@mui/material";
import type { EditorView } from "@tiptap/pm/view";
import { EditorContent,useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
	forwardRef,
	type MouseEvent,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";

import { SAMPLE_RESOURCES } from "@/components/global/components/tiptap/domain/resources";
import { ParagraphResourceMeta } from "@/components/global/components/tiptap/extensions/paragraph-resource-meta/paragraph-resource-meta";
import {
	ResourceSlotsPrune,
	type ResourceOptionsForPruneRef,
} from "@/components/global/components/tiptap/extensions/paragraph-resource-meta/resource-slots-prune";
import { FormulaTokenHighlight } from "@/components/global/components/tiptap/extensions/resource-token/formula-highlight-extension";
import { ResourceSuggestionPlugin } from "@/components/global/components/tiptap/extensions/suggestion/suggestion-plugin";
import type {
	ReactionResourceOption,
	SuggestionState,
} from "@/components/global/components/tiptap/extensions/suggestion/suggestion-types";
import { filterResourcesByQuery } from "@/components/global/components/tiptap/extensions/suggestion/suggestion-filter";
import type { FormulaVariableFormulaPayload, FormulaVariableResourceRef } from "@/components/rule-canvas-builder/utils/formula-variable-payload";
import { dedupeFormulaResources, pruneFormulaResourcesByFormula } from "@/components/rule-canvas-builder/utils/formula-variable-payload";

import { SuggestionsMenu } from "./SuggestionsMenu";

function formulaTokenDisplayInner(resource: ReactionResourceOption): string {
	if (resource.resourceType === "screenField" || resource.resourceType === "advancedTrigger") {
		return resource.label;
	}
	return resource.value;
}

function dedupeReactionResourceOptions(options: ReactionResourceOption[]): ReactionResourceOption[] {
	const byKey = new Map<string, ReactionResourceOption>();
	for (const o of options) {
		byKey.set(`${o.resourceType}:${o.value}`, o);
	}
	return [...byKey.values()];
}

function mergeResourceOptionsForPrune(
	base: ReactionResourceOption[],
	picked: ReactionResourceOption[],
): ReactionResourceOption[] {
	return dedupeReactionResourceOptions([...base, ...picked]);
}

function registerPickedResource(
	resource: ReactionResourceOption,
	baseOptions: ReactionResourceOption[],
	pickedRef: { current: ReactionResourceOption[] },
	effectiveRef: ResourceOptionsForPruneRef,
) {
	pickedRef.current = dedupeReactionResourceOptions([...pickedRef.current, resource]);
	effectiveRef.current = mergeResourceOptionsForPrune(baseOptions, pickedRef.current);
}

export interface FormulaTextAreaHandle {
  insertAtCursor: (resource: ReactionResourceOption) => void;
  insertAtEnd: (text: string) => void;
}

export type FormulaTextAreaChangePayload = FormulaVariableFormulaPayload;

interface FormulaTextAreaProps {
  value?: string;
  resources?: FormulaVariableResourceRef[];
  onChange?: (payload: FormulaVariableFormulaPayload) => void;
  availableResources?: ReactionResourceOption[];
}

function toResourceRef(resource: ReactionResourceOption): FormulaVariableResourceRef {
	return {
		resourceType: resource.resourceType,
		resourceIdentifier:
			resource.resourceType === "advancedTrigger"
				? (resource.triggerName ?? resource.value)
				: resource.value,
	};
}

function resourceRefListsEqual(
	a: FormulaVariableResourceRef[],
	b: FormulaVariableResourceRef[],
): boolean {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (
			a[i].resourceType !== b[i].resourceType ||
      a[i].resourceIdentifier !== b[i].resourceIdentifier
		) {
			return false;
		}
	}
	return true;
}

const LEGEND_ITEMS = [
	{ label: "{Resources}", color: "#2E7D32" },
	{ label: "\"Constants\"", color: "#D32F2F" },
	{ label: "FUNCTIONS()", color: "#1565C0" },
	{ label: "Operator", color: "#000000" },
	{ label: "=", color: "#000000" },
] as const;

export const FormulaTextArea = forwardRef<FormulaTextAreaHandle, FormulaTextAreaProps>(
	function FormulaTextArea({ value, resources, onChange, availableResources }, ref) {

		const [suggestionState, setSuggestionState] = useState<SuggestionState | null>(null);
		const [highlightedIndex, setHighlightedIndex] = useState(0);
		const containerRef = useRef<HTMLDivElement | null>(null);

		const menuStateRef = useRef({
			suggestion: null as SuggestionState | null,
			filteredResources: [] as ReactionResourceOption[],
			highlightedIndex: 0,
		});

		const insertResourceRef = useRef<((resource: ReactionResourceOption) => void) | null>(null);

		const resourcesRef = useRef<FormulaVariableResourceRef[]>(resources ?? []);

		const resourceOptions = useMemo(
			() => (availableResources?.length ? availableResources : SAMPLE_RESOURCES),
			[availableResources]
		);
		const pickedLookupRef = useRef<ReactionResourceOption[]>([]);
		const resourceOptionsRef = useRef(resourceOptions);
		useEffect(() => {
			resourceOptionsRef.current = mergeResourceOptionsForPrune(
				resourceOptions,
				pickedLookupRef.current,
			);
		}, [resourceOptions]);

		const filteredResources = useMemo(
			() =>
				suggestionState
					? filterResourcesByQuery(resourceOptions, suggestionState.query)
					: [],
			[resourceOptions, suggestionState],
		);

		useEffect(() => {
			if (!filteredResources.length && highlightedIndex !== 0) {
				setHighlightedIndex(0);
				return;
			}
			const maxIndex = filteredResources.length - 1;
			if (filteredResources.length > 0 && highlightedIndex > maxIndex) {
				setHighlightedIndex(maxIndex);
			}
		}, [filteredResources.length, highlightedIndex]);

		useEffect(() => {
			if (!suggestionState) setHighlightedIndex(0);
		}, [suggestionState]);

		const getContainerRect = useCallback(
			() => containerRef.current?.getBoundingClientRect() ?? null,
			[]
		);

		const handleSuggestionKeyDown = useCallback(
			(_view: EditorView, event: KeyboardEvent) => {
				const { suggestion, filteredResources, highlightedIndex } =
          menuStateRef.current;
				if (!suggestion) return false;

				if (event.key === "Escape") {
					event.preventDefault();
					setSuggestionState(null);
					return true;
				}

				if (!filteredResources.length) return false;

				if (event.key === "ArrowDown") {
					event.preventDefault();
					setHighlightedIndex((p) => (p + 1) % filteredResources.length);
					return true;
				}
				if (event.key === "ArrowUp") {
					event.preventDefault();
					setHighlightedIndex(
						(p) => (p - 1 + filteredResources.length) % filteredResources.length
					);
					return true;
				}
				if (event.key === "Enter" || event.key === "Tab") {
					event.preventDefault();
					const resource = filteredResources[highlightedIndex];
					if (resource) insertResourceRef.current?.(resource);
					return true;
				}
				return false;
			},
			[]
		);

		const editorExtensions = useMemo(
			() => [
				StarterKit.configure({
					heading: false,
					hardBreak: false,
					bold: false,
					italic: false,
					strike: false,
					code: false,
					codeBlock: false,
					blockquote: false,
					bulletList: false,
					orderedList: false,
					listItem: false,
					horizontalRule: false,
					paragraph: false,
				}),
				ParagraphResourceMeta.configure({
					HTMLAttributes: { class: "tiptap-paragraph" },
				}),
				ResourceSlotsPrune.configure({
					resourceOptionsRef,
				}),
				FormulaTokenHighlight,
				ResourceSuggestionPlugin.configure({
					onUpdate: setSuggestionState,
					onKeyDown: handleSuggestionKeyDown,
					getContainerRect,
				}),
			],
			[handleSuggestionKeyDown, getContainerRect]
		);

		const onChangeRef = useRef(onChange);
		useEffect(() => {
			onChangeRef.current = onChange;
		}, [onChange]);

		const editor = useEditor({
			content: value ?? "",
			extensions: editorExtensions,
			onUpdate({ editor }) {
				const formula = editor.getText();
				const pruned = pruneFormulaResourcesByFormula(
					formula,
					resourcesRef.current,
					resourceOptionsRef.current,
				);
				resourcesRef.current = pruned;
				onChangeRef.current?.({ formula, resources: pruned });
			},
		});

		useEffect(() => {
			if (!editor) return;
			const currentText = editor.getText();
			if (value !== currentText) {
				editor.commands.setContent(value ?? "", false);
			}
			const formula = editor.getText();
			const fromProps = resources ?? [];
			const pruned = pruneFormulaResourcesByFormula(
				formula,
				fromProps,
				resourceOptionsRef.current,
			);
			resourcesRef.current = pruned;
			if (!resourceRefListsEqual(pruned, fromProps)) {
				onChangeRef.current?.({ formula, resources: pruned });
			}
		}, [editor, value, resources, resourceOptions]);

		const handleContainerMouseDown = useCallback(
			(event: MouseEvent<HTMLDivElement>) => {
				if (!editor) return;
				const target = event.target as HTMLElement;
				if (target?.closest(".ProseMirror")) return;
				event.preventDefault();
				editor.chain().focus().run();
			},
			[editor]
		);

		const insertResource = useCallback(
			(resource: ReactionResourceOption) => {
				if (!editor || !suggestionState) return;
				registerPickedResource(resource, resourceOptions, pickedLookupRef, resourceOptionsRef);
				const inner = formulaTokenDisplayInner(resource);
				resourcesRef.current = dedupeFormulaResources([
					...resourcesRef.current,
					toResourceRef(resource),
				]);
				editor
					.chain()
					.focus()
					.insertContentAt(suggestionState.range, `{${inner}} `)
					.run();
				setSuggestionState(null);
			},
			[editor, resourceOptions, suggestionState]
		);

		const insertAtCursor = useCallback(
			(resource: ReactionResourceOption) => {
				if (!editor) return;
				registerPickedResource(resource, resourceOptions, pickedLookupRef, resourceOptionsRef);
				resourcesRef.current = dedupeFormulaResources([
					...resourcesRef.current,
					toResourceRef(resource),
				]);
				editor
					.chain()
					.focus()
					.insertContent(`{${formulaTokenDisplayInner(resource)}} `)
					.run();
			},
			[editor, resourceOptions]
		);

		const insertAtEnd = useCallback(
			(text: string) => {
				if (!editor) return;
				const end = editor.state.doc.content.size;
				editor.chain().focus().insertContentAt(end, `${text} `).run();
			},
			[editor]
		);

		useImperativeHandle(ref, () => ({ insertAtCursor, insertAtEnd }), [insertAtCursor, insertAtEnd]);

		menuStateRef.current = {
			suggestion: suggestionState,
			filteredResources,
			highlightedIndex,
		};
		insertResourceRef.current = insertResource;

		const showSuggestions = Boolean(suggestionState);

		return (
			<Box>
				<Box sx={{ mb: 1 }}>
					<Typography
						variant="caption"
						sx={{ color: "text.secondary", display: "block", mb: 1 }}
					>
						Type <b>{"{"}</b> to summon dropdowns for resources, operators and functions into your formulas.
					</Typography>
					<Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
						{LEGEND_ITEMS.map((item) => (
							<Typography
								key={item.label}
								variant="body2"
								sx={{ color: item.color, fontWeight: 600 }}
							>
								{item.label}
							</Typography>
						))}
					</Stack>
				</Box>

				<Box
					ref={containerRef}
					onMouseDown={handleContainerMouseDown}
					sx={{
						position: "relative",
						border: "1px solid",
						borderColor: "rgba(13, 31, 48, 0.14)",
						borderRadius: 2,
						padding: 1.5,
						backgroundColor: "background.paper",
						minHeight: 160,
						cursor: "text",
						transition: "border-color 0.18s, box-shadow 0.18s",
						"&:hover": {
							borderColor: "rgba(2, 213, 171, 0.5)",
						},
						"&:focus-within": {
							borderColor: "#02D5AB",
							boxShadow: "0 0 0 3px rgba(2, 213, 171, 0.18)",
						},
					}}
				>
					{editor && (
						<EditorContent
							editor={editor}
							className="tiptap-content-container formula-text-area-editor"
							style={{ minHeight: 120 }}
						/>
					)}

					{showSuggestions && suggestionState && (
						<SuggestionsMenu
							resources={filteredResources}
							highlightedIndex={highlightedIndex}
							position={{
								top: (containerRef.current?.getBoundingClientRect().top ?? 0) + suggestionState.position.top,
								left: (containerRef.current?.getBoundingClientRect().left ?? 0) + suggestionState.position.left,
							}}
							onSelect={insertResource}
						/>
					)}
				</Box>
			</Box>
		);
	}
);

export default FormulaTextArea;
